import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { PATH_CLIENT_EDIT, PATH_CLIENT_FACILITY_EDIT } from 'routing/path'
import PageHeader from 'components/page-header'
import ValidatedField from 'components/controls/validated-field';
import ValidatedMaskedField from 'components/controls/validated-masked-field';
import { showSuccessNotification, showErrorNotification } from "actions/action-complete-notificator";
import { useGetFacilityQuery, useGetNewFacilityQuery, useAddFacilityMutation, useSaveFacilityMutation } from 'actions/client/client-facility-api';
import { translationApi  } from 'actions/translation-api';
import { tryParseInt } from 'utils/number-utils';

const ClientFacilityEdit = () => {
	const navigate = useNavigate();
	const params = useParams();
	const clientIdString = params.clientId;
	const clientId = tryParseInt(clientIdString);
	const clientFacilityIdString = params.clientFacilityId;
	const clientFacilityId = tryParseInt(clientFacilityIdString);

	const { data: facility, isLoading: loadFacilityInProgress, isFetching: fetchFacilityInProgress } = useGetFacilityQuery(clientFacilityId, clientFacilityId ? { refetchOnMountOrArgChange: true } : { skip: true } );
	const { data: newFacility, isLoading: loadNewFacilityInProgress, isFetching: fetchNewFacilityInProgress } = useGetNewFacilityQuery(clientId, clientFacilityId ? { skip: true } : { refetchOnMountOrArgChange: true } );
	const [ addFacility, { isLoading: addFacilityInProgress }] = useAddFacilityMutation();
	const [ saveFacility, { isLoading: saveFacilityInProgress }] = useSaveFacilityMutation();
	const translations = useSelector(translationApi.endpoints.getTranslations.select()).data ?? {};

	if (clientFacilityIdString && !loadFacilityInProgress && !fetchFacilityInProgress && !facility) {
		// facility load finished, but facility not found
		navigate("/404");
		return;
	}

	const getPageHeader = () => {
		if (clientFacilityId) {
			// existing client facility pending
			if (loadFacilityInProgress || fetchFacilityInProgress) {
				return <PageHeader title="..." breadcrumbs={[]} breadcrumbsCurrentPage="..." />
			}

			const breadcrumbs = [
				{ name: `${translations.client} ${facility?.clientName}`, link: PATH_CLIENT_EDIT + facility?.clientId },
			];
			return <PageHeader title={facility.name} breadcrumbs={breadcrumbs} breadcrumbsCurrentPage={`${translations.clientFacility} ${facility.name}`} />
		}
		else {
			// new client facility pending
			if (loadNewFacilityInProgress || fetchNewFacilityInProgress) {
				return <PageHeader title="..." breadcrumbs={[]} breadcrumbsCurrentPage="..." />
			}

			const breadcrumbs = [
				{ name: `${translations.client} ${newFacility?.clientName}`, link: PATH_CLIENT_EDIT + newFacility?.clientId },
			];
			let title = `${translations.newClientFacility} (${newFacility?.clientName})`;
			return <PageHeader title={title} breadcrumbs={breadcrumbs} breadcrumbsCurrentPage={translations.newClientFacility} />
		}
	}

	const initializeClientFacility = clientFacilityId && !loadFacilityInProgress && !fetchFacilityInProgress;
	const initialValues = {
		name: initializeClientFacility ? facility.name : "",
		mainAddressCity: initializeClientFacility ? (facility.address?.city ?? "") : "",
		mainAddressPostalCode: initializeClientFacility ? (facility.address?.postalCode ?? "") : "",
		mainAddressStreet: initializeClientFacility ? (facility.address?.street ?? "") : ""
	};
	
	const validationSchema = Yup.object().shape({
		name: Yup.string()
			.required(translations.warningFieldRequired),
		mainAddressPostalCode: Yup.string()
			.matches(/^\d{2}-\d{3}$/, translations.warningValueInvalid),
	});
	
	const submitDisabled = addFacilityInProgress || saveFacilityInProgress;
	const handleSubmit = async (fields) => {
		const clientFacilityToUpsert = {
			name: fields.name,
			address: {
				city: fields.mainAddressCity,
				postalCode: fields.mainAddressPostalCode,
				street: fields.mainAddressStreet
			}
		};
		
		if (clientFacilityId) {
			clientFacilityToUpsert.clientFacilityId = clientFacilityId;
			const result = await saveFacility(clientFacilityToUpsert);
			if ('error' in result) {
				showErrorNotification(result.error);
			}
			else {
				showSuccessNotification(translations.changesSaved);
			}
		}
		else {
			clientFacilityToUpsert.clientId = clientId;
			const result = await addFacility(clientFacilityToUpsert);
			if ('error' in result) {
				showErrorNotification(result.error);
			}
			else {
				showSuccessNotification(translations.clientFacilityAdded);
				navigate(PATH_CLIENT_FACILITY_EDIT + result.data);
				return;
			}
		}
	}

	const postalCodeInputMask = [/\d/, /\d/, '-', /\d/, /\d/, /\d/];
	const randomAutoComplete = Math.random().toString(36).replace(/[^a-z]+/g, "").substr(0, 5);
	return (
		<div>
			{ getPageHeader() }
			{ <Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={ async (fields) => { await handleSubmit(fields) }}>
				{props => (
					<Form autoComplete={randomAutoComplete}>
					<div className="col-12 mb-4">
						<div className="card border-light shadow-sm components-section">
							<div className="card-body">
								<h2 className="h5 mb-4">{translations.basicData}</h2>
								<div className="row mb-4">
									<div className="col-lg-4 col-sm-6">
										<ValidatedField name="name" label={translations.name} {...props} />
									</div>
								</div>
								<h2 className="h5 mb-4">Adres</h2>
								<div className="row mb-4">
									<div className="col-lg-4 col-sm-6">
										<ValidatedField name="mainAddressStreet" label={translations.street} {...props} />
									</div>
									<div className="col-lg-4 col-sm-6">
										<ValidatedMaskedField name="mainAddressPostalCode" label={translations.postalCode} mask={postalCodeInputMask} {...props} />
									</div>
									<div className="col-lg-4 col-sm-6">
										<ValidatedField name="mainAddressCity" label={translations.city} {...props} />
									</div>
								</div>
								<div className="row mb-5 mb-lg-5">
									<div className="col-lg-3 col-md-6">
										<div className="form-group mb-3">
											<button id="saveButton" type="submit" className="btn btn-primary me-2" disabled={submitDisabled ? "disabled" : null} >{translations.actionSave}</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Form>)}
			</Formik>}
		</div>
	)
} 

export default ClientFacilityEdit;
